import { FC, useRef } from 'react';
import { Setting } from '../App';
import { SUBSCRIPTION_NAME } from '../constants';

import { useLogging } from '../hooks/common/useLogging';
import { useLoadingAny } from '../hooks/common/useLoadingAny';
import { useEmitPrepared } from '../hooks/common/useEmitPrepared';
import { useEmitResetWindow } from '../hooks/common/useEmitResetWindow';

import { useSummary } from '../hooks/SubscriptionDetail/useSummary';
import { useDeliveryDate } from '../hooks/SubscriptionDetail/useDeliveryDate';
import { useRegisteredProduct } from '../hooks/SubscriptionDetail/useRegisteredProducts';
import { useSubscriptionSkip } from "../hooks/SubscriptionDetail/useSubscriptionSkip";
import { useSubscriptionPause } from '../hooks/SubscriptionDetail/useSubscriptionPause';
import { useDeliveryPlan } from '../hooks/SubscriptionDetail/useDeliveryPlan';
import { useSubscriptionStatus } from '../hooks/SubscriptionDetail/useSubscriptionStatus';
import { useSubscriptionLineAdd } from '../hooks/SubscriptionDetail/useSubscriptionLine';

import { PausedInformation } from '../components/SubscriptionDetail/PausedInformation';
import { Remarks } from '../components/SubscriptionDetail/Remarks';
import { Menu } from '../components/SubscriptionDetail/Menu';
import { Grid } from '../components/common/Grid';
import { HorizontalRule } from '../components/common/HorizontalRule';
import { DeliveryInformation } from '../components/SubscriptionDetail/DeliveryInformation';
import { SectionTitle } from '../components/common/SectionTitle';
import { NextProducts } from '../components/SubscriptionDetail/NextProducts';
import { PresaleProducts } from '../components/SubscriptionDetail/GridProducts';
import { PreviousMemberInformation } from '../components/SubscriptionDetail/PreviousMemberInformation';

export const NewSubscriptionDetail: FC<{
  setting: Setting;
}> = ({ setting }) => {

  const { token, extendData, subscriptionRef } = setting;
  const [{ subscription, loadingSubscriptions }, { getSubscription, setSubscription } ] = subscriptionRef;
  const registeredProducts = useRegisteredProduct(subscription);
  const { isNoProduct, isPaused, isPreviousMember } = useSubscriptionStatus(subscription);

  // サブスク商品変更関連
  const [ loadingSubscriptionLineAdd, addSubscriptionLine ] = 
    useSubscriptionLineAdd(subscription, token, getSubscription);

  // 合計金額
  const summary = useSummary(subscription);

  // 配送日時関連
  const [ 
    {dates, loadingUpdateDeliveryDays},
    updateDeliveryDays 
  ] = useDeliveryDate(subscription, token, getSubscription);

  // 配送周期関連
  const [
    { planGroups, activePlanGroups, currentPlan, loadingModifyDeliveryPlan },
    modifyDeliveryPlan
   ] = useDeliveryPlan(subscription, setSubscription, token);

  // サブスクスキップ関連
  const [
    { loadingSkipSubscription, loadingSkipCancelSubscription, isAllowSkipCancel },
    { skipSubscription, skipCancelSubscription }
  ] = useSubscriptionSkip(subscription, setSubscription, token);

  // サブスク一時停止関連
  const [
    loadingPauseSubscription,
    { pauseSubscription, pauseCancelSubscription }
  ] = useSubscriptionPause(subscription, setSubscription, token);

  // どれかのAPIが通信中であればfalseになる
  // 下層componentのボタンのdisabledに利用する
  const loadingAny = useLoadingAny([
    loadingSubscriptions,
    loadingSubscriptionLineAdd,
    loadingSkipSubscription,
    loadingPauseSubscription,
    loadingSkipCancelSubscription,
    loadingUpdateDeliveryDays,
    loadingModifyDeliveryPlan
  ]);

  // 読み込み完了イベントを親に送信する
  useEmitPrepared();

  // iframeのheightは基本的に固定のため、iframe内要素が変更された場合に
  // 適時親にiframe内のdocumentHeightを伝え、heightを更新する
  const rootElementReference = useRef<HTMLDivElement | null>(null);
  useEmitResetWindow(rootElementReference, [subscription]);

  useLogging('SubscriptionDetail', [subscription, extendData]);

  return (
    <div 
      className={`subscriptionDetail appContainer`} 
      ref={rootElementReference}
    >
      {!subscription ? (
        <></>
      ) : isPreviousMember ? (
        <PreviousMemberInformation
          loadingAny={loadingAny}
          pauseCancel={pauseCancelSubscription}
        />
      ) : isPaused ? (
        <PausedInformation
          summary={summary}
          dates={dates}
          registeredProducts={registeredProducts}
          pauseCancel={pauseCancelSubscription}
          loadingAny={loadingAny}
        />
      ) : (
        <>
          <Remarks subscription={subscription} />

          <Grid>
            <div>
              <SectionTitle>次回お届け情報</SectionTitle>
              <DeliveryInformation 
                subscription={subscription}
                summary={summary}
                dates={dates}
                updateDeliveryDays={updateDeliveryDays}
                skip={skipSubscription}
                skipCancel={skipCancelSubscription}
                pause={pauseSubscription}
                isAllowSkipCancel={isAllowSkipCancel}
                activePlanGroups={activePlanGroups}
                currentPlan={currentPlan}
                loadingAny={loadingAny}
                modifyDeliveryPlan={modifyDeliveryPlan}
              />
            </div>
            <div>
              <SectionTitle>次回お届け予定商品</SectionTitle>
              <NextProducts 
                subscription={subscription}
                extendData={extendData}
                add={addSubscriptionLine}
              />
            </div>
          </Grid>

          {!isNoProduct && (extendData?.presaleProducts.length || 0) > 0 && (
            <>
              <HorizontalRule />

              <SectionTitle
                badge={`${SUBSCRIPTION_NAME}契約者限定！`}
                description='定期便契約者限定の特別オファーのご案内です。'
              >特別オファー商品一覧</SectionTitle>

              <PresaleProducts
                extendData={extendData}
              />
            </>
          )}
        </>
      )}

      <HorizontalRule />
      <Menu subscription={subscription} extendData={extendData} />
    </div>
  )
}